import { LayoutPanelLeft, TableProperties } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

export type ListView = 'split' | 'table';

const STORAGE_PREFIX = 'doclist.view.';

/**
 * The split/table choice is a property of the person and the screen, not of
 * the link, so it lives in localStorage rather than the URL — and storage may
 * be unavailable (private mode), in which case the choice just does not stick.
 */
export function readStoredView(storageKey: string): ListView {
  try {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (stored === 'split' || stored === 'table') return stored;
  } catch {
    /* storage unavailable — the default below is the answer anyway */
  }
  return 'split';
}

export function writeStoredView(storageKey: string, view: ListView): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, view);
  } catch {
    /* same as above */
  }
}

/** Two pressed-state buttons: list-and-detail, or the full-width table. */
export function ViewToggle({ view, onChange }: { view: ListView; onChange: (view: ListView) => void }) {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={t('doclist.view')} className="inline-flex gap-1">
      <Button
        type="button"
        variant={view === 'split' ? 'secondary' : 'ghost'}
        size="sm"
        aria-pressed={view === 'split'}
        onClick={() => onChange('split')}
      >
        <LayoutPanelLeft aria-hidden className="rtl:-scale-x-100" /> {t('doclist.viewSplit')}
      </Button>
      <Button
        type="button"
        variant={view === 'table' ? 'secondary' : 'ghost'}
        size="sm"
        aria-pressed={view === 'table'}
        onClick={() => onChange('table')}
      >
        <TableProperties aria-hidden /> {t('doclist.viewTable')}
      </Button>
    </div>
  );
}
